/* eslint-disable */
import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Stack,
  Chip,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { BORDER_RADIUS_SM } from "../../theme/theme";
import Editor from "@monaco-editor/react";
import OntologyDiagram from "../OntologyDiagram";

export default function OntologyTabPanel(props: any) {
  const {
    view,
    selectedOrganization,
    organisationList,
    handleChangeForAzure,
    ontologyTabValue,
    setOntologyTabValue,
    ontologyFileContent,
    setOntologyFileContent,
    shaclFileContent,
    setShaclFileContent,
    lastSavedOntology,
    ontologyMeta,
    shaclMeta,
    lastSavedShacl,
    isEditing,
    setIsEditing,
    isFetching,
    isSaving,
    uploadingType,
    ontologyInputRef,
    shaclInputRef,
    ontologyHasUnsavedChanges,
    shaclHasUnsavedChanges,
    currentTabHasUnsavedChanges,
    handleSaveOntology,
    handleFileUpload,
  } = props;

  return (
    <Box
      id="manage-ontology-tab"
      sx={{
        p: { xs: 2, lg: 3 },
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", lg: "calc(100vh - 240px)" },
      }}
    >
      {/* Org selector — only shown when rendered as a global-admin tab */}
      {view === "global-admin-ontology" && (
        <FormControl variant="outlined" fullWidth sx={{ mb: 3 }}>
          <InputLabel id="ontology-org-select-label">Organisation</InputLabel>
          <Select
            labelId="ontology-org-select-label"
            value={selectedOrganization}
            onChange={handleChangeForAzure}
            label="Organisation"
          >
            <MenuItem value="All Organisation">All Organisation</MenuItem>
            {organisationList?.map((org: any) => (
              <MenuItem key={org._id} value={org._id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Top row: Upload buttons + last-saved metadata */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          {/* Hidden file inputs */}
          <input
            type="file"
            accept=".ttl"
            ref={ontologyInputRef}
            style={{ display: "none" }}
            onChange={(e) => handleFileUpload(e, "ontology")}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => ontologyInputRef.current?.click()}
            sx={{
              fontWeight: "bold",
              borderRadius: BORDER_RADIUS_SM,
            }}
            disabled={uploadingType !== null || isSaving}
          >
            {uploadingType === "ontology"
              ? "Uploading..."
              : "Upload ontology (.ttl)"}
          </Button>

          <input
            type="file"
            accept=".shacl,.ttl"
            ref={shaclInputRef}
            style={{ display: "none" }}
            onChange={(e) => handleFileUpload(e, "shacl")}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => shaclInputRef.current?.click()}
            sx={{
              fontWeight: "bold",
              borderRadius: BORDER_RADIUS_SM,
            }}
            disabled={uploadingType !== null || isSaving}
          >
            {uploadingType === "shacl" ? "Uploading..." : "Upload SHACL (.ttl)"}
          </Button>
        </Stack>

        {/* Last saved info */}
        {(ontologyMeta || shaclMeta) && (
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            {ontologyTabValue === 0 && ontologyMeta?.updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Ontology last saved:{" "}
                {new Date(ontologyMeta.updatedAt).toLocaleString()}
                {ontologyMeta.lastEditedBy &&
                  ` by ${ontologyMeta.lastEditedBy.firstName ?? ""} ${ontologyMeta.lastEditedBy.lastName ?? ""}`.trim()}
              </Typography>
            )}
            {ontologyTabValue === 1 && shaclMeta?.updatedAt && (
              <Typography variant="caption" color="text.secondary">
                SHACL last saved:{" "}
                {new Date(shaclMeta.updatedAt).toLocaleString()}
                {shaclMeta.lastEditedBy &&
                  ` by ${shaclMeta.lastEditedBy.firstName ?? ""} ${shaclMeta.lastEditedBy.lastName ?? ""}`.trim()}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Editor Card */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          flex: { xs: "none", lg: 1 },
          overflow: "hidden",
        }}
      >
        {/* Sub-tab header */}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
            pt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tabs
            value={ontologyTabValue}
            onChange={(e, val) => {
              setOntologyTabValue(val);
            }}
            aria-label="ontology editor tabs"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                minWidth: { xs: 80, sm: 100 },
              },
            }}
          >
            <Tab
              label={
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  Ontology
                  {ontologyHasUnsavedChanges && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                      }}
                    />
                  )}
                </Box>
              }
            />
            <Tab
              label={
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                >
                  SHACL
                  {shaclHasUnsavedChanges && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                      }}
                    />
                  )}
                </Box>
              }
            />
          </Tabs>

          {/* Edit pencil */}
          {ontologyTabValue === 1 && (
            <Tooltip title={isEditing ? "Editing..." : "Edit SHACL"}>
              <span>
                <IconButton
                  onClick={() => setIsEditing(true)}
                  disabled={isEditing || isFetching}
                  color="primary"
                >
                  <Edit />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Editor body */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: { xs: "none", lg: 1 },
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {isFetching ? (
            <Box
              sx={{
                height: "400px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={32} />
              <Typography color="text.secondary">
                Loading from database...
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: { xs: "70vh", lg: "100%" },
                width: "100%",
                overflow: "hidden",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {ontologyTabValue === 0 ? (
                <OntologyDiagram
                  key={`${selectedOrganization}-ontology`}
                  initialTtl={ontologyFileContent || undefined}
                  onTtlChange={(newTtl: string) =>
                    setOntologyFileContent(newTtl)
                  }
                />
              ) : (
                <Editor
                  height="100%"
                  defaultLanguage="xml"
                  value={shaclFileContent || ""}
                  onChange={(val) => {
                    setShaclFileContent(val ?? "");
                  }}
                  options={{
                    readOnly: !isEditing,
                    minimap: { enabled: false },
                    fontSize: 14,
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    automaticLayout: true,
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Footer: unsaved badge + Save / Discard */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left: unsaved changes indicator */}
          <Box>
            {currentTabHasUnsavedChanges ? (
              <Chip
                size="small"
                label="Unsaved changes"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <Chip
                size="small"
                label="All changes saved"
                color="success"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          {/* Right: action buttons */}
          <Stack
            direction="row"
            spacing={2}
            justifyContent={{ xs: "stretch", sm: "flex-end" }}
          >
            {/* Discard — reloads from DB */}
            <Button
              variant="outlined"
              color="inherit"
              sx={{ borderColor: "#ddd", color: "#333", px: 3 }}
              disabled={
                isSaving || isFetching || !currentTabHasUnsavedChanges
              }
              onClick={() => {
                if (ontologyTabValue === 0) {
                  setOntologyFileContent(lastSavedOntology);
                } else {
                  setShaclFileContent(lastSavedShacl);
                  setIsEditing(false);
                }
              }}
            >
              Discard
            </Button>

            {/* Save to MongoDB */}
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSaveOntology()}
              sx={{ px: 4, fontWeight: 700 }}
              disabled={
                isSaving || isFetching || !currentTabHasUnsavedChanges
              }
              startIcon={
                isSaving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {isSaving ? "SAVING..." : "SAVE TO DATABASE"}
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}
